import { z } from 'zod';
/**
 * Wire shapes for the DECISION.md §2.1 rule: money NEVER travels as a JS
 * number. Every amount crosses the API as a decimal STRING, exactly what a
 * NUMERIC column produces and what Money.of() accepts; parseFloat anywhere on
 * the path is the bug these schemas exist to keep out.
 *
 * GEN-49 — "Every Money, Decimal and Percent field must state its decimal
 * places in this document. A field with unstated decimals must not be built."
 * The document keeps its half globally (GEN-96: stored money has exactly 2
 * decimals; GEN-101: quantities 3, unit prices 4, percentages 2) and names
 * the exceptions field by field (GEN-102's 4dp cost, MD-03 field 19's 6dp
 * conversion factor). These schemas are the build's half — the refusal that
 * implements "must not be built": each shape's regex IS a field's stated
 * decimal places carried onto the wire, so building a new Money, Percent or
 * Quantity field means copying a number the document must first supply. A
 * field the document gives no places has no bound to write here, which is
 * the rule doing its work at the moment of building. Fields whose places
 * differ carry their own bounded schema beside their own spec citation
 * (CostStringSchema and the factor schema in items.ts).
 */
/** A non-negative money amount: up to 13 digits, at most 2 decimals (NUMERIC(15,2)). */
export const MoneyStringSchema = z
    .string()
    .regex(/^\d{1,13}(\.\d{1,2})?$/, 'Amounts are decimal strings with at most 2 decimals');
/**
 * A percentage 0..100, at most 2 decimals (the two-way boxes of F-2/F-4).
 * The bound is checked on the STRING — money-adjacent values never become JS
 * numbers, not even to validate them (§2.1).
 */
export const PctStringSchema = z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Percentages are decimal strings with at most 2 decimals')
    .refine((value) => {
    const [integer = '', fraction = ''] = value.split('.');
    if (integer.length < 3)
        return true;
    // Three-digit integer parts compare lexicographically = numerically.
    return integer === '100' ? /^0*$/.test(fraction) : integer < '100';
}, 'A discount must be between 0 and 100 percent.');
/**
 * A quantity: positive, at most 3 decimals. The WIRE limit — how many decimals
 * a quantity may actually carry is the unit's business (`N-4`), and
 * `BR-MD-12-11` refuses to store more than `units.precision` allows.
 */
export const QtyStringSchema = z
    .string()
    .regex(/^\d{1,12}(\.\d{1,3})?$/, 'Quantities are decimal strings with at most 3 decimals')
    .refine((value) => !/^0+(\.0+)?$/.test(value), 'Quantity must be more than zero.');
/**
 * `units.precision` on the wire — `N-4`'s missing half.
 *
 * `N-4`: "Quantities must be shown with the number of decimals the ITEM'S UNIT
 * OF MEASURE allows, and ALWAYS with the unit written next to them." The
 * decimals are a property of the unit, not of the column: `numeric(18,3)` is
 * storage, `precision` is the business rule, and a screen that does not
 * receive `precision` cannot obey `N-4` however careful its formatter is.
 *
 * So every row that already carries `unitSymbol` for the second half of the
 * rule carries this for the first. Range from `ERR-MD-12-07`, "A unit allows
 * between 0 and 3 decimals", and enforced identically by the column's CHECK.
 */
export const UnitPrecisionSchema = z.number().int().min(0).max(3);
/**
 * A currency code on the wire — `BR-MD-11-01`, "a currency code must be exactly
 * three capital letters and must be unique", and `ERR-MD-11-01`'s sentence for
 * the half this schema can see ("A currency code is exactly three letters, like
 * USD."). Uniqueness is the database's `currencies_tenant_code_key`.
 *
 * ── WHY THIS IS A PRIMITIVE AND NOT SIX PRIVATE CONSTANTS ──────────────────
 *
 * It WAS six private constants: `partners.ts` had a file-local `CurrencySchema`,
 * `accounts.ts` a file-local `CurrencyOptionalSchema`, `banking.ts` three inline
 * copies, `warehouses.ts` one — and `hr.ts` and `imports.ts` had `length(3)`,
 * which is a DIFFERENT rule: it accepts `usd` and `u$d` and leaned on a
 * `.toUpperCase()` in the service to make the first of those look right. Six
 * copies of a regex is six chances for one screen to quietly accept a code the
 * others refuse, and two of them had already taken it.
 *
 * WHAT THIS SCHEMA IS NOT. It is the SHAPE of a code, never a claim that the
 * tenant has that currency: `MYR` passes here and is refused by
 * `assertCurrencyUsable` in the API, which is the only thing that reads the
 * `currencies` table. A three-letter regex cannot know what a tenant switched
 * off (`BR-DATA-06`), and a wire schema must not pretend it can.
 */
export const CurrencySchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'A currency code is exactly three letters, like USD.');
/**
 * The same code where a column is nullable and NULL carries a meaning —
 * `accounts.currency`, whose `FIN-01` field 6 is "optional account currency;
 * NULL means the base currency". Nullish rather than nullable because an
 * omitted field and a cleared one are the same request on that form.
 */
export const CurrencyNullishSchema = CurrencySchema.nullish();
/**
 * ISO calendar date (YYYY-MM-DD) — dates carry no clock (PART 4 §4.10).
 *
 * GEN-88/GEN-89's storage-and-entry half is this schema plus the native
 * `type="date"` inputs: every date is stored and typed as YYYY-MM-DD, and
 * the banned US MM/DD/YYYY form appears nowhere. RECORDED DIVERGENCE on the
 * display half: GEN-89 wants read-only dates drawn as "MMM D, YYYY", and
 * the build's tables draw the ISO form in an LTR mono island (GEN-90) —
 * a product-wide display decision the tables have carried from the start;
 * re-skinning it is an owner call, flagged in the rule-queue report. Where
 * dates ARE formatted (audit stamps, notification rows) the month names
 * come from the locale, never a hard-coded list (GEN-91a), and the native
 * picker opens on the box's own month (GEN-91) while still accepting typing
 * (GEN-52 — <input type="date"> is both at once).
 */
export const IsoDateSchema = z.iso.date();
/**
 * D46 — a refusal's named arguments, as they travel on the wire. Strings and
 * numbers only: everything a refusal names (a code, an amount, an item, a
 * count) is one of the two, and keeping the union closed is what lets the
 * client hand `params` straight to the ICU formatter without a sanitising
 * pass.
 */
export const RefusalParamsSchema = z.record(z.string(), z.union([z.string(), z.number()]));
/**
 * `key`/`params` are D46's optional structured half: present only on
 * converted parameterised refusals, ignored by every consumer that predates
 * them. `message` stays required — that is the backward-compatibility
 * promise, and `structured-refusal-seam.test.ts` asserts the fallback works
 * rather than merely that it exists.
 */
export const MessageSchema = z
    .object({
    message: z.string(),
    key: z.string().optional(),
    params: RefusalParamsSchema.optional(),
})
    .strict();
/**
 * MD-RULE-5 — "Every master record must show who created it and when, and who rule-coverage:ignore
 * last changed it and when."
 *
 * ── SIX FIELDS, NOT FOUR, AND THE NAMES ARE THE POINT ─────────────────────
 *
 * The rule's verb is SHOW. A uuid is not showable, so the id alone would leave
 * the screen with nothing to draw and MD-RULE-5 still unmet after all the rule-coverage:ignore
 * server work. The name is resolved server-side — one query per response
 * rather than one per row — following `LeadActivitySchema`'s
 * `createdBy` + `createdByName` pair, which is the in-house convention.
 *
 * ── EVERY FIELD IS NULLABLE, AND NULL HAS A MEANING TO RENDER ─────────────
 *
 * The columns behind these are nullable with no default, deliberately: they
 * were added by 20260901100000 to tables that already held rows, and a
 * backfilled default would have named people as authors of records they never
 * touched. So NULL means "not recorded", and there are three ongoing sources
 * of it besides history:
 *
 *   * the record predates the columns;
 *   * the SYSTEM made the change — a bounced cheque raising a customer's flag
 *     writes NULL into `updatedBy` on purpose (BR-FIN-10-17);
 *   * nobody created the row at all: `number_sequences` rows are minted at
 *     signup by `app_ensure_number_sequences()`, so `createdBy` there is
 *     permanently NULL.
 *
 * THE UI MUST RENDER NULL AS ITS OWN ANSWER — "Not recorded", "System" — and
 * never as a blank that reads like a name which failed to load. An id present
 * with a NULL name means the user row was removed; that is a different state
 * again and should not silently become an empty cell.
 *
 * `createdAt` is always present. `updatedAt` is NOT NULL on every master
 * table except `accounts`, whose `updated_at` arrived late (20260902100000)
 * and nullable — a default would have stamped migration time as a change
 * time on every existing row. `accounts.ts` therefore spreads this shape
 * with `updatedAt` overridden to nullable; every other master kind carries
 * it verbatim.
 */
export const AuthorshipSchema = z.object({
    createdBy: z.uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    updatedBy: z.uuid().nullable(),
    updatedByName: z.string().nullable(),
    updatedAt: z.string(),
});
//# sourceMappingURL=primitives.js.map